import React, { createContext, useContext, useEffect, useState, ReactNode, useRef, useCallback } from 'react';
import { createApp } from '@shopify/app-bridge';
import { useAuth } from './AuthContext';

interface ShopifyContextType {
  app: any;
  isShopifyAdmin: boolean;
  shop: string | null;
  host: string | null;
  initializeAppBridge: () => Promise<void>;
}

interface ShopifyProviderProps {
  children: ReactNode;
}

const ShopifyContext = createContext<ShopifyContextType | undefined>(undefined);

// Global AppBridge state to persist across re-renders
let appBridgeInstance: any = null;
let appBridgeInitialized = false;
let globalShop: string | null = null;
let globalHost: string | null = null;
let forcePersist = false;

export const ShopifyProvider: React.FC<ShopifyProviderProps> = ({ children }) => {
  const [app, setApp] = useState<any>(null);
  const [isShopifyAdmin, setIsShopifyAdmin] = useState(false);
  const [shop, setShop] = useState<string | null>(null);
  const [host, setHost] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const { isLoading } = useAuth();
  
  const initializationRef = useRef(false);

  const initializeAppBridge = useCallback(async () => {
    if (isInitializing) {
      return;
    }

    if (appBridgeInstance && appBridgeInitialized) {
      setApp(appBridgeInstance);
      setIsShopifyAdmin(true);
      setShop(globalShop);
      setHost(globalHost);
      return;
    }

    if (forcePersist) {
      return;
    }

    setIsInitializing(true);

    try {
      // Get configuration from URL or stored config
      const urlParams = new URLSearchParams(window.location.search);
      let shopParam = urlParams.get('shop');
      let hostParam = urlParams.get('host');

      console.log('[DEBUG] ShopifyContext - Initial URL params:', {
        shop: shopParam,
        host: hostParam,
        fullUrl: window.location.href,
        search: window.location.search
      });

      // If not in URL, try to get from sessionStorage (for navigation persistence)
      if (!shopParam || !hostParam) {
        shopParam = sessionStorage.getItem('shopify_shop') || shopParam;
        hostParam = sessionStorage.getItem('shopify_host') || hostParam;
        console.log('[LOG] Retrieved from sessionStorage - Shop:', shopParam, 'Host:', hostParam);
      }

      // If still missing parameters, try to extract from embedded parameters (same as authService)
      if (!shopParam || !hostParam) {
        // Use the same logic as authService.getEmbeddedParams()
        let embeddedUrlParams = new URLSearchParams(window.location.search);
        
        // If no parameters in URL, check sessionStorage for preserved parameters
        if (!embeddedUrlParams.toString()) {
          const preservedParams = sessionStorage.getItem('login_url_params');
          if (preservedParams) {
            embeddedUrlParams = new URLSearchParams(preservedParams);
          }
        }
        
        if (!shopParam) {
          shopParam = embeddedUrlParams.get('shop');
        }
        if (!hostParam) {
          hostParam = embeddedUrlParams.get('host');
        }
        
        console.log('[LOG] Extracted from embedded params - Shop:', shopParam, 'Host:', hostParam);
      }

      // CRITICAL: For AppBridge, we MUST use the host parameter as-is (base64 encoded)
      // AppBridge expects the exact base64-encoded value from the URL
      // DO NOT DECODE - this was causing the "not a valid host" error
      if (hostParam) {
        console.log('[LOG] VERSION 4.0 - Using host parameter as-is (base64):', hostParam);
        // AppBridge requires the base64-encoded host parameter exactly as provided by Shopify
      }

      // Enhanced Shopify admin detection and parameter extraction
      const currentUrl = window.location.href;
      const referrerUrl = document.referrer;
      
      console.log('[LOG] Current URL:', currentUrl);
      console.log('[LOG] Referrer URL:', referrerUrl);
      
      const isInShopifyAdmin = currentUrl.includes('admin.shopify.com') || 
                              currentUrl.includes('myshopify.com') ||
                              (referrerUrl && referrerUrl.includes('admin.shopify.com'));

      // If we're in Shopify admin but missing parameters, try to extract them
      if (isInShopifyAdmin && (!shopParam || !hostParam)) {
        console.log('[DEBUG] In Shopify admin but missing parameters, attempting extraction...');
        
        // Try to extract shop from current URL
        let shopMatch = currentUrl.match(/store\/([^.]+)\.myshopify\.com/);
        if (!shopMatch && referrerUrl) {
          // Try referrer URL
          shopMatch = referrerUrl.match(/store\/([^.]+)\.myshopify\.com/);
        }
        
        // If still no match, try to extract from the embedded parameters
        if (!shopMatch) {
          // Check if we have embedded parameters that might contain shop info
          const embeddedParams = new URLSearchParams(window.location.search);
          const hmac = embeddedParams.get('hmac');
          if (hmac) {
            // Try to extract shop from the URL path or other available info
            const urlPath = window.location.pathname;
            const pathMatch = urlPath.match(/\/([^.]+)\.myshopify\.com/);
            if (pathMatch) {
              shopMatch = pathMatch;
            }
          }
        }
        
        if (shopMatch && !shopParam) {
          shopParam = `${shopMatch[1]}.myshopify.com`;
          console.log('[LOG] Extracted shop from URL:', shopParam);
        }

        // Generate host parameter if missing
        if (!hostParam && shopParam) {
          hostParam = btoa(`${shopParam}/admin`).replace(/[^a-zA-Z0-9]/g, '');
          console.log('[LOG] Generated host parameter:', hostParam);
        }

        // Store parameters for future navigation
        if (shopParam && hostParam) {
          sessionStorage.setItem('shopify_shop', shopParam);
          sessionStorage.setItem('shopify_host', hostParam);
          console.log('[LOG] ✅ Stored parameters in sessionStorage:', {
            shop: shopParam,
            host: hostParam
          });
        } else {
          console.log('[ERROR] Failed to extract or generate parameters:', {
            shopParam,
            hostParam,
            currentUrl,
            referrerUrl
          });
        }
      }

      // FALLBACK: If we still don't have parameters, try to extract them from the iframe URL pattern
      if (!shopParam || !hostParam) {
        console.log('[DEBUG] Attempting fallback parameter extraction...');
        
        // Try to extract from the current URL using the iframe pattern
        const urlMatch = currentUrl.match(/[?&]shop=([^&]+)/);
        const hostMatch = currentUrl.match(/[?&]host=([^&]+)/);
        
        if (urlMatch && !shopParam) {
          shopParam = decodeURIComponent(urlMatch[1]);
          console.log('[LOG] Fallback: Extracted shop from URL pattern:', shopParam);
        }
        
        if (hostMatch && !hostParam) {
          hostParam = decodeURIComponent(hostMatch[1]);
          console.log('[LOG] Fallback: Extracted host from URL pattern:', hostParam);
        }
        
        // If we found parameters via fallback, store them
        if (shopParam && hostParam) {
          sessionStorage.setItem('shopify_shop', shopParam);
          sessionStorage.setItem('shopify_host', hostParam);
          console.log('[LOG] ✅ Fallback: Stored parameters in sessionStorage:', {
            shop: shopParam,
            host: hostParam
          });
        }
      }

      console.log('[LOG] Final parameters before AppBridge initialization:', {
        shopParam,
        hostParam,
        isInShopifyAdmin
      });

      // CRITICAL FIX: Always store parameters in sessionStorage if they are available
      // This ensures that the TestPromptModal can access them later
      if (shopParam && hostParam) {
        sessionStorage.setItem('shopify_shop', shopParam);
        sessionStorage.setItem('shopify_host', hostParam);
        console.log('[LOG] ✅ CRITICAL FIX: Stored parameters in sessionStorage:', {
          shop: shopParam,
          host: hostParam
        });
      } else {
        console.log('[ERROR] CRITICAL FIX: Failed to store parameters in sessionStorage:', {
          shopParam,
          hostParam
        });
      }

      // Set global variables for persistence
      if (shopParam) {
        globalShop = shopParam;
        setShop(shopParam);
      }
      if (hostParam) {
        globalHost = hostParam;
        setHost(hostParam);
      }

      // Initialize AppBridge if we have the required parameters
      if (shopParam && hostParam) {
        console.log('[LOG] ✅ Initializing AppBridge with shop:', shopParam, 'host:', hostParam);
        
        const app = createApp({
          apiKey: import.meta.env.VITE_SHOPIFY_API_KEY || 'a7cb75270aafc7d0198bb44eec53e8cd',
          host: hostParam,
          forceRedirect: false
        });

        appBridgeInstance = app;
        setApp(app);
        appBridgeInitialized = true;
        setIsShopifyAdmin(true);
        forcePersist = true;
        
        console.log('[LOG] ✅ AppBridge initialized successfully');
      } else {
        console.log('[WARNING] ⚠️ Missing shop or host parameters for AppBridge initialization');
        setIsShopifyAdmin(false);
      }
    } catch (error) {
      console.error('[ERROR] Failed to initialize AppBridge:', error);
      setIsShopifyAdmin(false);
    } finally {
      setIsInitializing(false);
    }
  }, [isInitializing, appBridgeInstance, appBridgeInitialized, forcePersist]);

  useEffect(() => {
    if (!isLoading && !initializationRef.current) {
      initializationRef.current = true;
      initializeAppBridge();
    }
  }, [isLoading, initializeAppBridge]);

  const value: ShopifyContextType = {
    app,
    isShopifyAdmin,
    shop,
    host,
    initializeAppBridge,
  };

  return <ShopifyContext.Provider value={value}>{children}</ShopifyContext.Provider>;
};

export const useShopify = (): ShopifyContextType => {
  const context = useContext(ShopifyContext);
  if (context === undefined) {
    throw new Error('useShopify must be used within a ShopifyProvider');
  }
  return context;
}; 